import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings, X, Save, Loader2 } from 'lucide-react';
import { STEPS } from './DiaryWizard';

import { ROLE_DISPLAY_NAMES as ROLE_LABELS } from '@/utils/roles';

export default function SectionAssignmentModal({
  sectionAssignments, teamMembers,
  assignSection, assignAllSections,
  saveSectionAssignments, sectionAssignSaving,
  onClose,
}) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="section-assign-modal">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white rounded-t-xl z-10">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-cyan-600" />
            <h2 className="text-lg font-bold text-slate-900">Diary Section Assignments</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-slate-500">Assign team members to specific diary sections. Each person will receive a daily reminder for their assigned sections. On smaller projects, assign one person to all sections.</p>

          {/* Assign All */}
          <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Assign All Sections To One Person</Label>
            <Select onValueChange={(v) => assignAllSections(v)} data-testid="assign-all-select">
              <SelectTrigger className="h-9"><SelectValue placeholder="Select a team member..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">-- Unassign All --</SelectItem>
                {teamMembers.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name || m.first_name || ''} {m.surname || m.last_name || ''} ({ROLE_LABELS[m.role] || m.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border-t pt-3">
            <Label className="text-xs font-semibold text-slate-700 mb-2 block">Individual Section Assignments</Label>
            <div className="space-y-2">
              {STEPS.filter(s => s.id !== 'ai_summary' && s.id !== 'submit').map(step => {
                const Icon = step.icon;
                const assigned = sectionAssignments[step.id];
                return (
                  <div key={step.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid={`section-assign-${step.id}`}>
                    <div className="flex items-center gap-2 w-28 shrink-0">
                      <Icon className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-xs font-medium text-slate-700">{step.label}</span>
                    </div>
                    <Select value={assigned?.user_id || '__none'} onValueChange={(v) => assignSection(step.id, v === '__none' ? null : v)}>
                      <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Not assigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Not assigned</SelectItem>
                        {teamMembers.map(m => (
                          <SelectItem key={m.id} value={m.id}>{m.name || m.first_name || ''} {m.surname || m.last_name || ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => { saveSectionAssignments(); onClose(); }} disabled={sectionAssignSaving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-section-assign-btn">
              {sectionAssignSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Save Assignments
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
